import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ImageOff } from 'lucide-react';
import type { Bucket, MediaItem } from '../../types';
import { Spinner } from './Primitives';

/**
 * The photo itself.
 *
 * Rotation is applied as a CSS transform — the file on disk is never touched.
 * At export time the same angle is written into the copy's EXIF marker instead.
 *
 * A rotated element keeps its original layout box, so `max-width: 100%` stops
 * constraining the visible result once the image is turned on its side. The size
 * is therefore computed rather than left to the browser: fit the photo into the
 * pane using whichever pair of axes it will actually occupy after the turn.
 */
export default function PreviewPane({
  item,
  url,
  buckets,
}: {
  item: MediaItem | null;
  url: string | null;
  buckets: Bucket[];
}) {
  const pane = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const element = pane.current;
    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      setBox({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => setNatural(null), [item?.id]);

  if (!item) {
    return (
      <div className="flex flex-1 items-center justify-center text-ink-600">
        <p className="text-caption">Nothing to show</p>
      </div>
    );
  }

  const bucket = buckets.find((b) => b.id === item.bucketId);
  const quarterTurned = item.rotation === 90 || item.rotation === 270;

  // Prefer the dimensions recorded at scan time; fall back to what loaded.
  const source =
    natural ?? (item.width && item.height ? { width: item.width, height: item.height } : null);

  let size: { width: number; height: number } | null = null;
  if (source && box.width > 0 && box.height > 0) {
    // After a quarter turn the photo's width occupies the pane's height.
    const occupiedWidth = quarterTurned ? source.height : source.width;
    const occupiedHeight = quarterTurned ? source.width : source.height;

    const scale = Math.min(box.width / occupiedWidth, box.height / occupiedHeight, 1_000);
    size = { width: source.width * scale, height: source.height * scale };
  }

  return (
    <div ref={pane} className="relative flex flex-1 items-center justify-center overflow-hidden p-4">
      {item.status !== 'ok' ? (
        <div className="flex flex-col items-center gap-3 text-ink-500">
          <ImageOff size={26} />
          <p className="text-caption">This file is no longer where it was.</p>
          <p className="font-mono text-tiny text-ink-600">{item.relPath}</p>
        </div>
      ) : url ? (
        <img
          key={item.id}
          src={url}
          alt={item.filename}
          draggable={false}
          onLoad={(event) =>
            setNatural({
              width: event.currentTarget.naturalWidth,
              height: event.currentTarget.naturalHeight,
            })
          }
          className={clsx(
            'select-none object-contain',
            !size && 'max-h-full max-w-full',
            'transition-[width,height] duration-100',
          )}
          style={{
            width: size ? `${size.width}px` : undefined,
            height: size ? `${size.height}px` : undefined,
            transform: item.rotation ? `rotate(${item.rotation}deg)` : undefined,
          }}
        />
      ) : (
        <Spinner className="h-5 w-5 text-ink-600" />
      )}

      {/* Decision badge — the one place colour appears over the photo. */}
      {bucket && (
        <div
          className={clsx(
            'pointer-events-none absolute left-5 top-5 rounded border px-2.5 py-1 text-tiny font-medium backdrop-blur',
            bucket.kind === 'keep'
              ? 'border-keep-line bg-keep-soft/90 text-keep'
              : 'border-reject-line bg-reject-soft/90 text-reject',
          )}
        >
          {bucket.name}
        </div>
      )}

      {item.rotation !== 0 && (
        <div className="pointer-events-none absolute right-5 top-5 rounded border border-ink-700 bg-ink-900/90 px-2.5 py-1 font-mono text-tiny text-ink-300 backdrop-blur">
          {item.rotation}°
        </div>
      )}
    </div>
  );
}
